import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useRouter } from 'next/router';
import { useStringTemplate } from '@blaze-cms/utils-handlebars';
import { HOVER, MOUSE_ENTER, MOUSE_LEAVE, HIDDEN } from '../../constants';
import { hasChildren } from '../../helpers';
import BlazeLink from '../BlazeLink';
import MenuContext from '../Menu/MenuContext';
import { injectHelperIntoTemplate } from './helpers';

const MenuItemRender = ({ children, eventType, text, modifier, url, parent }) => {
  const [displayChildren, setDisplayChildren] = useState(false);
  const { showMobileMenu } = useContext(MenuContext);
  const {
    loading: loadingText,
    data: [textToUse]
  } = useStringTemplate(parent, [text]);
  const {
    loading: loadingUrl,
    data: [urlToUse]
  } = useStringTemplate(parent, [injectHelperIntoTemplate(url, 'url_encode')]);

  const router = useRouter();
  const isHoverEvent = eventType === HOVER;
  const childrenDisplayClass = displayChildren ? '' : HIDDEN;
  const hasValidChildren = hasChildren(children);

  useEffect(() => {
    if (!showMobileMenu) setDisplayChildren(false);
  }, [showMobileMenu]);

  useEffect(() => {
    if (isHoverEvent) {
      setDisplayChildren(false);
    }
  }, [router.asPath, isHoverEvent]);

  if (loadingUrl || loadingText) return '';

  const handleItemEvent = ({ type }) => {
    if (isHoverEvent) {
      if (type === MOUSE_ENTER) {
        setDisplayChildren(true);
      }
      if (type === MOUSE_LEAVE) {
        setDisplayChildren(false);
      }
    }
  };

  const handleMobileClick = () => {
    if (!urlToUse && hasValidChildren) {
      setDisplayChildren(!displayChildren);
    }
  };

  return (
    <li className={modifier} onMouseEnter={handleItemEvent} onMouseLeave={handleItemEvent}>
      <div
        className="menu--item--link"
        onClick={handleMobileClick}
        role={!urlToUse && hasValidChildren ? 'button' : undefined}
        tabIndex={!urlToUse && hasValidChildren ? 0 : undefined}>
        {urlToUse ? <BlazeLink href={urlToUse}>{textToUse}</BlazeLink> : <span>{textToUse}</span>}

        {hasValidChildren && (
          <i
            role="button"
            className="menu--item--link--icon"
            onClick={e => {
              e.stopPropagation();
              setDisplayChildren(!displayChildren);
            }}
            tabIndex={0}>
            {displayChildren ? <FaChevronUp /> : <FaChevronDown />}
          </i>
        )}
      </div>

      {hasValidChildren && (
        <div className={`menu--item-children ${childrenDisplayClass}`}>{children}</div>
      )}
    </li>
  );
};

MenuItemRender.propTypes = {
  modifier: PropTypes.string,
  text: PropTypes.string,
  url: PropTypes.string,
  eventType: PropTypes.string.isRequired,
  parent: PropTypes.object,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

MenuItemRender.defaultProps = {
  url: '',
  modifier: '',
  text: '',
  children: null,
  parent: {}
};

export default MenuItemRender;
